import { ChevronDown, ChevronLeft, ChevronUp, Maximize2, X } from 'lucide-react';
import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useScreen } from '../lib/layout.ts';
import { useModalFocus } from '../lib/modal-focus.ts';
import { cn } from '../lib/utils.ts';
import { Button } from './ui/button.tsx';
import { CardTitle } from './ui/card-title.tsx';
import { UnsavedScope, useUnsavedStore } from '../lib/unsaved.tsx';
import { Icon } from './ui/icon.tsx';

/** Steht der Inhalt im Seitenpanel? (Datensatz-Köpfe lassen dort den Chart weg, Rudolf 20.10.2026.) */
const InPanelContext = createContext(false);

export function useInRecordPanel(): boolean {
  return useContext(InPanelContext);
}

/**
 * Seitenpanel (DESIGN „Seitenpanel“, EXPERIENCE §4.1, §6.2, §10.4; Story 1.23): Karte rechts über volle Höhe, am Tablet
 * 90 vw, am Desktop `{spacing.panel-width}` (45 vw; Rudolf 20.10.2026) breit; am Handy als eigene Seite (Vollbild) mit „Zurück“ und ↑ ↓.
 * Geöffnet wird es am Tablet; ändert sich danach die Größe, bleibt dasselbe Panel samt Formularen stehen und wechselt nur
 * die Darstellung. Liste dahinter mit `{colors.scrim}` abgedunkelt. Kopf: Titel, Symbolknöpfe ↑ ↓ „Vollansicht“ ✕.
 * Die Knöpfe ↑ ↓ springen zum vorigen/nächsten Datensatz; Esc (nicht in Feldern), ✕ oder Tippen daneben schließt,
 * danach Fokus zurück auf das zuvor fokussierte Element. Beim Öffnen und Blättern Fokus in den Kopf.
 * Ohne Datensatz-Blättern (`onPrev`/`onNext` fehlen) und ohne `onFull` entfallen ↑ ↓ bzw. „Vollansicht“ – so nutzt es
 * z. B. der Verlauf einer Einstellungs-Karte (Story 1.27).
 *
 * Design S3 H2 (Ticket 1.129, Muster `auftrag-panel-s3.html`): Panel ganz weiß (`card`); Karten im Panel stehen flach,
 * nur durch eine feine Linie getrennt (`data-panel-surface`, styles.css). „Vollansicht“ als Pille mit Text.
 */
export function RecordPanel({
  title,
  recordKey,
  onPrev,
  onNext,
  onFull,
  onClose,
  label = 'Seitenpanel',
  width = 'wide',
  children,
}: {
  title: ReactNode;
  /** Schlüssel des gezeigten Datensatzes (Fokus in den Kopf beim Blättern). */
  recordKey: string;
  /** Vorheriger bzw. nächster Datensatz; `null` = keiner (Knopf gesperrt). */
  onPrev?: (() => void) | null;
  onNext?: (() => void) | null;
  onFull?: () => void;
  onClose: () => void;
  /** Name des Dialogs für Hilfsmittel. */
  label?: string;
  /**
   * Breite am Desktop (Ticket 1.245, opt-in): `wide` = 45 vw (Rudolf 20.10.2026, vorher 60 vw); `panel` = Token `--panel-w` aus dem Entwurf
   * (Aufträge, Entwürfe); `sheet` = min(1100 px, 80 vw) für breite Tabellen (Aus Excel einfügen, Sichtung 29.10.2026).
   * Tablet (90 vw) und Handy (Vollbild) bleiben unverändert.
   */
  width?: 'wide' | 'panel' | 'sheet';
  children: ReactNode;
}) {
  // Ungespeicherte Änderungen im Panel (EXPERIENCE §4.3): Schließen und Blättern fragen zuerst.
  const unsaved = useUnsavedStore();
  // Darstellung je Größe; Inhalt bleibt an derselben Stelle im Baum (Größenwechsel verliert keine Eingaben).
  const screen = useScreen();
  const guarded = (action: (() => void) | null) => (action ? () => unsaved.attempt(action) : null);
  const close = () => unsaved.attempt(onClose);
  const browse = onPrev !== undefined || onNext !== undefined;
  const prev = guarded(onPrev ?? null);
  const next = guarded(onNext ?? null);
  const full = onFull ? () => unsaved.attempt(onFull) : null;
  const panel = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  // Fokus kehrt beim Schließen dorthin zurück, wo er beim Öffnen stand (Zeile bzw. Titel-Link).
  const opener = useRef<HTMLElement | null>(typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null));
  // Esc im Feld (Formular, E-Mail) schließt das Panel nicht: ungespeicherte Eingaben bleiben.
  useModalFocus(panel, opener, close, () => heading.current, { escapeInFields: false });

  useEffect(() => {
    heading.current?.focus();
  }, [recordKey]);

  // Design S3 H2 (Muster auftrag-panel-s3.html): runde Knöpfe mit Linie auf weißem Grund, „Vollansicht“ als Pille mit
  // Text, Schließen dunkelblau.
  // Redesign 31.10.2026 (Variante C): ruhige Symbolknöpfe ohne Füllung, Schließen ebenso; Dunkelblau bleibt dem Hauptknopf.
  const iconButton =
    'grid size-9 flex-none cursor-pointer place-items-center rounded-full text-ink-2 transition-colors duration-[var(--motion-fast)] hover:bg-hover hover:text-ink disabled:cursor-default disabled:opacity-[var(--opacity-dim)] disabled:hover:bg-transparent pointer-coarse:size-11';

  return createPortal(
    <>
      <div data-testid="panel-abdunklung" aria-hidden className="fixed inset-0 z-40 bg-scrim" onClick={close} />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-testid="seitenpanel"
        data-presentation={screen}
        data-panel-surface=""
        className={cn(
          'fixed z-40 flex flex-col bg-background motion-reduce:animate-none',
          screen === 'phone'
            ? 'inset-0'
            : cn('inset-y-0 right-0 animate-panel-in border-l border-line shadow-panel', screen === 'tablet' ? 'w-[90vw]' : width === 'panel' ? 'w-panel-w max-w-full' : width === 'sheet' ? 'w-sheet-w' : 'w-[45vw]'),
        )}
      >
        <div className={cn('flex flex-none items-center gap-space-2 border-b border-line py-space-3', screen === 'phone' ? 'px-space-4' : 'px-space-5')}>
          {screen === 'phone' ? (
            <Button variant="secondary" onClick={close} className="flex-none">
              <Icon icon={ChevronLeft} size="md" aria-hidden />
              Zurück
            </Button>
          ) : null}
          {/* Redesign N40 (Sichtung HS): die Leiste trägt nur ↑ ↓ Vollansicht ✕ – Nummer und Name stehen einmal im Kopf darunter;
              der Titel bleibt für Hilfsmittel und den Fokus beim Öffnen. */}
          <div className="min-w-0 flex-1">
            <div className="sr-only">
              <CardTitle inline focusRef={heading}>
                {title}
              </CardTitle>
            </div>
          </div>
          <div className="flex flex-none items-center gap-space-2">
            {browse ? (
              <>
                <button type="button" aria-label="Vorheriger Datensatz" disabled={!prev} onClick={() => prev?.()} className={iconButton}>
                  <Icon icon={ChevronUp} size="md" aria-hidden />
                </button>
                <button type="button" aria-label="Nächster Datensatz" disabled={!next} onClick={() => next?.()} className={iconButton}>
                  <Icon icon={ChevronDown} size="md" aria-hidden />
                </button>
              </>
            ) : null}
            {screen === 'phone' ? null : (
              <>
                {full ? (
                  <button
                    type="button"
                    onClick={full}
                    className="inline-flex h-9 flex-none cursor-pointer items-center gap-space-2 rounded-full border border-line px-space-3 text-row font-medium whitespace-nowrap text-ink transition-colors duration-[var(--motion-fast)] hover:bg-hover pointer-coarse:h-11"
                  >
                    <Maximize2 size={13} strokeWidth={2} aria-hidden />
                    Vollansicht
                  </button>
                ) : null}
                <button
                  type="button"
                  aria-label="Panel schließen"
                  aria-keyshortcuts="Escape"
                  onClick={close}
                  className="grid size-9 flex-none cursor-pointer place-items-center rounded-full border border-line text-ink-2 transition-colors duration-[var(--motion-fast)] hover:bg-hover hover:text-ink pointer-coarse:size-11"
                >
                  <Icon icon={X} size="md" aria-hidden />
                </button>
              </>
            )}
          </div>
        </div>
        <div data-panel-body="" className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain pt-space-4 pb-space-8', screen === 'phone' ? 'px-space-4' : 'px-space-5')}>
          <InPanelContext.Provider value={true}>
            <UnsavedScope store={unsaved}>{children}</UnsavedScope>
          </InPanelContext.Provider>
        </div>
      </div>
    </>,
    document.body,
  );
}
